import JSZip from "jszip";
import { supabase } from "@/integrations/supabase/client";
import { getDocumentStoragePath } from "@/lib/documentDownload";

export interface VaultExportDoc {
  id: string;
  document_name: string | null;
  document_type: string | null;
  file_path: string | null;
  file_url: string | null;
  signed_file_path: string | null;
  signed_file_url: string | null;
}

export interface VaultExportProgress {
  current: number;
  total: number;
  label: string;
}

export interface VaultExportResult {
  blob: Blob;
  filename: string;
  included: number;
  skipped: string[];
}

function sanitizeSegment(s: string, fallback = "Document"): string {
  const cleaned = (s || fallback).replace(/[\\/:*?"<>|]+/g, "-").trim();
  return cleaned || fallback;
}

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Build a zip of every document the current session can read for the given
 * UNA that has a stored file, grouped by vault_lane from the registry.
 * Fetches signed URLs at run time (no service role, no pre-baked URLs).
 * Missing PDFs are skipped, never fatal.
 */
export async function buildVaultZip(
  unaId: string,
  associationName: string,
  onProgress?: (p: VaultExportProgress) => void,
): Promise<VaultExportResult> {
  const [{ data: docsData, error: docsErr }, { data: regData }] = await Promise.all([
    supabase
      .from("generated_documents")
      .select(
        "id, document_name, document_type, file_path, file_url, signed_file_path, signed_file_url",
      )
      .eq("una_id", unaId)
      .order("generated_at", { ascending: false }),
    supabase.from("stew_document_registry").select("document_type, vault_lane"),
  ]);
  if (docsErr) throw new Error(docsErr.message);

  const laneByType = new Map<string, string>();
  (regData || []).forEach((r: any) => {
    if (r?.document_type && r?.vault_lane) {
      laneByType.set(r.document_type, r.vault_lane);
    }
  });

  const docs = ((docsData || []) as VaultExportDoc[]).filter(
    (d) => !!getDocumentStoragePath(d as any, { signed: true }),
  );

  const zip = new JSZip();
  const skipped: string[] = [];
  const usedNames = new Set<string>();
  let included = 0;

  for (let i = 0; i < docs.length; i++) {
    const doc = docs[i];
    const name = doc.document_name || `document-${doc.id}`;
    onProgress?.({ current: i + 1, total: docs.length, label: name });

    const path = getDocumentStoragePath(doc as any, { signed: true });
    if (!path) {
      skipped.push(name);
      continue;
    }

    try {
      const { data: signed, error: signErr } = await supabase.storage
        .from("documents")
        .createSignedUrl(path, 300);
      if (signErr || !signed?.signedUrl) throw signErr || new Error("no url");
      const res = await fetch(signed.signedUrl, { cache: "no-store" });
      if (!res.ok) throw new Error(`http ${res.status}`);
      const blob = await res.blob();

      const lane = sanitizeSegment(
        laneByType.get(doc.document_type || "") || "General",
        "General",
      );
      const base = sanitizeSegment(name, "Document");
      let filename = `${lane}/${base}.pdf`;
      let n = 1;
      while (usedNames.has(filename)) {
        n += 1;
        filename = `${lane}/${base} (${n}).pdf`;
      }
      usedNames.add(filename);
      zip.file(filename, blob);
      included += 1;
    } catch (e) {
      console.warn("Vault export skipped", doc.id, e);
      skipped.push(name);
    }
  }

  const blob = await zip.generateAsync({ type: "blob" });
  const filename = `${sanitizeSegment(associationName, "Association")} Record Export ${todayIso()}.zip`;
  return { blob, filename, included, skipped };
}

export function triggerBlobDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = url;
  link.download = filename;
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
